import { useEffect, useRef, useState, type ReactNode } from "react";
import {
  ArrowCounterClockwise,
  ArrowUUpLeft,
  ArrowUp,
  Drop,
  HandGrabbing,
} from "@phosphor-icons/react";
import { KarelCanvas, type KarelCanvasHandle } from "../../ide/KarelCanvas";
import { Button } from "../../app/components/ui/Button";
import { useLessonFinish } from "./useLessonFinish";
import { useThemeStore, type LanguageMode } from "../../app/stores/themeStore";
import { type ButtonLessonConfig } from "../engine/lessonTypes";
import styles from "./LessonView.module.css";

interface ButtonLessonViewProps extends ButtonLessonConfig {
  onFinished: () => void;
}

const COMMAND_LABELS: Record<LanguageMode, Record<string, string>> = {
  js: {
    move: "move();",
    turnLeft: "turnLeft();",
    putBeeper: "putBeeper();",
    pickBeeper: "pickBeeper();",
  },
  python: {
    move: "move()",
    turnLeft: "turn_left()",
    putBeeper: "put_beeper()",
    pickBeeper: "pick_beeper()",
  },
};

const COMMAND_ICONS: Record<string, ReactNode> = {
  move: <ArrowUp weight="bold" size={16} />,
  turnLeft: <ArrowCounterClockwise weight="bold" size={16} />,
  putBeeper: <Drop weight="fill" size={16} />,
  pickBeeper: <HandGrabbing weight="fill" size={16} />,
};

export function ButtonLessonView({
  buttons,
  world,
  solution,
  onFinished,
}: ButtonLessonViewProps) {
  const goalRef = useRef<KarelCanvasHandle>(null);
  const workRef = useRef<KarelCanvasHandle>(null);
  const [ready, setReady] = useState(false);
  const languageMode = useThemeStore((s) => s.languageMode);
  const commandLabels = COMMAND_LABELS[languageMode];
  const { scheduleFinish } = useLessonFinish(onFinished, true);

  useEffect(() => {
    setReady(false);
    const timer = setTimeout(() => {
      goalRef.current?.animateCommands(solution, () => setReady(true));
    }, 1000);
    return () => clearTimeout(timer);
  }, [solution]);

  const check = (action: () => void): void => {
    const work = workRef.current?.getIde();
    const goal = goalRef.current?.getIde();
    if (!work || !goal) return;
    action();
    if (work.getModel().equals(goal.getModel())) {
      scheduleFinish();
    }
  };

  const actions: Record<string, () => void> = {
    move: () => check(() => workRef.current?.getIde()?.stepMove()),
    turnLeft: () => check(() => workRef.current?.getIde()?.stepTurnLeft()),
    putBeeper: () => check(() => workRef.current?.getIde()?.stepPutBeeper()),
    pickBeeper: () => check(() => workRef.current?.getIde()?.stepPickBeeper()),
  };

  const activeCommands = Object.keys(buttons).filter((key) => buttons[key]);

  const handleReset = (): void => {
    void workRef.current?.getIde()?.stopButton();
  };

  return (
    <div className={styles.lesson}>
      <div className={styles.buttonLesson}>
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>Goal</h2>
          <div className={styles.worldCanvas}>
            <KarelCanvas ref={goalRef} world={world} />
          </div>
        </div>
        <div className={styles.commandsPanel}>
          <h2 className={styles.panelTitle}>Commands</h2>
          <div className={styles.commands}>
            {activeCommands.map((key) => (
              <Button
                key={key}
                icon={COMMAND_ICONS[key]}
                disabled={!ready}
                onClick={actions[key]}
              >
                {commandLabels[key] ?? key}
              </Button>
            ))}
          </div>
          <Button
            variant="secondary"
            icon={<ArrowUUpLeft weight="bold" size={16} />}
            disabled={!ready}
            onClick={handleReset}
          >
            Reset
          </Button>
        </div>
        <div className={styles.panel}>
          <h2 className={styles.panelTitle}>World</h2>
          <div className={styles.worldCanvas}>
            <KarelCanvas ref={workRef} world={world} />
          </div>
        </div>
      </div>
    </div>
  );
}
